//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { Asserts, InferType } from 'yup'
import {
  yupEthAddrRequired,
  LoanLifeCycleState,
  LoanStateToString,
  LoanTransitionStateToString,
  yupApr,
  yupErc20WeiRequired,
  LoanTransitionState,
} from './Enums'
import { EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

import { APR, ERC20Wei } from './conversions'
import { ZeroAddress } from 'ethers'

export const LoanCTOSchema = yup
  .object({
    id: yup
      .string<EthAddr>()
      .label('LoanID loanAddr')
      .required()
      .default('ID_NOT_SET')
      .meta(toFieldMetaData({ propName: 'id', description: 'ID of Loan ', stringType: StringTypeEnum.ID })),
    symbol: yup
      .string()
      .label('Symbol')
      .required()
      .default(' ')
      .meta(toFieldMetaData({ propName: 'symbol', description: 'Symbol for vault tokens' })),

    loanAddr: yupEthAddrRequired
      .label('Loan Address')
      .required()
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'loanAddr',
          description:
            ' The Ethereum contract address for the created/requested/active/matured loan. Will be 0x...00000 for loans in Requested State. ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    borrowerAddr: yupEthAddrRequired
      .nullable()
      .label('Borrower Address')
      .required()
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'borrowerAddr',
          description:
            ' The Ethereum wallet address for the Borrower the created/requested/active/matured loan. Will 0x...00000 for loans in Requested State. ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    fundingVaultAddr: yupEthAddrRequired
      .required()
      .label('Funding Vault')
      .required()
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'fundingVaultAddr',
          description:
            ' The Ethereum contract address for the Funding Vault of the created/requested/active/matured loan Will be 0x...00000 for loans in Requested State ',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),
    poolAddr: yupEthAddrRequired
      .required()
      .label('Pool Address')
      .default(ZeroAddress)
      .meta(
        toFieldMetaData({
          propName: 'poolAddr',
          description: ' The smart-contract address of the loan vault.',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),

    state: yup
      .number<LoanLifeCycleState>()
      .oneOf(Object.values(LoanLifeCycleState).filter((v) => typeof v === 'number') as number[])
      .defined()
      .default(LoanLifeCycleState.Requested)
      .label('Loan Status')
      .meta(
        toFieldMetaData({
          propName: 'state',
          description:
            'Current status of the loan in its lifecycle: Requested (0), Canceled (1), Funded (2), Matured (3), Active (4), or Settled (5)',
          stateToString: LoanStateToString,
        })
      ),
    transitionState: yup
      //.mixed<LoanTransitionState>()
      .number<LoanTransitionState>()
      .oneOf(Object.values(LoanTransitionState).filter((v) => typeof v === 'number') as number[])
      .defined()
      .default(LoanTransitionState.Requested)
      .label('Loan Transition Status')
      .meta(
        toFieldMetaData({
          propName: 'transitionState',
          description:
            'Current status of the loan in its transition lifecycle: Requested (0), ApprovedForDeposits (1), TransitioningFundsIn (2), AccruingInterest (3), RedemptionsClosed (4), TransitioningFundsOut (5), or RedemptionsReleased (6)',
          stateToString: LoanTransitionStateToString,
        })
      ),
    durationDays: yup
      .number()
      .required()
      .default(0)
      .label('Loan Duration')
      .meta(
        toFieldMetaData({
          propName: 'durationDays',
          description: ' The duration in days of the loan; also known as loan tenor. ',
          stringType: StringTypeEnum.TIME_INTERVAL_DAYS,
        })
      ),
    principal: yupErc20WeiRequired
      .required()
      .default('0')
      .label('Principal Amount')
      .meta(
        toFieldMetaData({
          propName: 'principal',
          description: ' The current principal for the loan in Wei ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    startingPrincipal: yupErc20WeiRequired
      .required()
      .default('0')
      .label('Starting Principal Amount')
      .meta(
        toFieldMetaData({
          propName: 'startingPrincipal',
          description: ' The principal for the loan at commencement in Wei ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    interest: yupErc20WeiRequired
      .required()
      .default('0')
      .label('Interest of Loan')
      .meta(
        toFieldMetaData({
          propName: 'interest',
          description: 'Accrued interest in USDC-1e6 up to the current point in the loan lifecycle',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    indicativeInterest: yupErc20WeiRequired
      .required()
      .default('0')
      .label('Indicative Interest of Loan')
      .meta(
        toFieldMetaData({
          propName: 'indicativeInterest',
          description: ' The amount of interest in USDC-1e6 expected to accrue if held to maturity ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    originationFee: yupErc20WeiRequired
      .required()
      .default('0')
      .label('Origination Fee Amount')
      .meta(
        toFieldMetaData({
          propName: 'originationFee',
          description: ' The sum of the Platform Fee and Advisor Fee in basis points ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    indicativeApr: yupApr
      .required()
      .default('0')
      .label('Indicative APR')
      .meta(
        toFieldMetaData({
          propName: 'indicativeApr',
          description: ' The indicative interest rate in basis points ',
          stringType: StringTypeEnum.APR,
        })
      ),
    finalizedApr: yupApr
      .required()
      .default('0')
      .label('Finalized APR')
      .meta(
        toFieldMetaData({
          propName: 'finalizedApr',
          description: ' The confirmed interest rate in basis points ',
          stringType: StringTypeEnum.APR,
        })
      ),

    exchangeRateAtDeposit: yupErc20WeiRequired
      .required()
      .default('0')
      .label('exchangeRateAtDeposit')
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateAtDeposit',
          description: ' The exchange rate in Wei of Vault Tokens to USDC at loan commencement 1e18 is 1:1 ration ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    exchangeRateAtMaturity: yupErc20WeiRequired
      .required()
      .default('0')
      .label('Exchange Rate At Maturity')
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateAtMaturity',
          description: ' The exchange rate in Wei of Vault Tokens to USDC at loan maturity 1e18 is 1:1 ration ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    fundingVaultBalance: yupErc20WeiRequired
      .label('Borrower Vault Balance')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'fundingVaultBalance',
          description: ' The USDC-1e6 balance of the funding vault ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    assetsRolloverToNextLoan: yupErc20WeiRequired
      .required()
      .default('0')
      .label('Assets Rollover To Next Loan')
      .meta(
        toFieldMetaData({
          propName: 'assetsRolloverToNextLoan',
          description: ' The amount in USDC-1e6 that is rolling over into the next loan cycle ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    assetsToReturnToPool: yupErc20WeiRequired
      .required()
      .default('0')
      .label('Assets To Return To Pool')
      .meta(
        toFieldMetaData({
          propName: 'assetsToReturnToPool',
          description: ' The amount in USDC-1e6 that needs repaid into the Loan Vault to meet withdrawals ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    accrualStartDayTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('Accrual Start Day Timestamp')
      .meta(
        toFieldMetaData({
          propName: 'accrualStartDayTimestamp',
          description: ' The Epoch Unix Timestamp in seconds for the Loan Commencement Date ',
          stringType: StringTypeEnum.ETH_DATE,
        })
      ),
    accrualStartTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('Accrual Start Timestamp')
      .meta(
        toFieldMetaData({
          propName: 'accrualStartTimestamp',
          description: ' The Epoch Unix Timestamp in seconds for the Loan Commencement Date ',
          stringType: StringTypeEnum.ETH_TIME,
        })
      ),

    transferInWindowDurationDays: yup
      .number()
      .required()
      .default(0)
      .label('Transfer In Window Duration')
      .meta(
        toFieldMetaData({
          propName: 'transferInWindowDurationDays',
          description: ' The duration of the Transfer In Window in number of days ',
          stringType: StringTypeEnum.TIME_INTERVAL_DAYS,
        })
      ),
    transferOutWindowDurationDays: yup
      .number()
      .required()
      .default(0)
      .label('Transfer Out Window Duration')
      .meta(
        toFieldMetaData({
          propName: 'transferOutWindowDurationDays',
          description: ' The duration of the Transfer Out Window in number of days ',
          stringType: StringTypeEnum.TIME_INTERVAL_DAYS,
        })
      ),

    depositClosingTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('depositClosingTimestamp')
      .meta(
        toFieldMetaData({
          propName: 'depositClosingTimestamp',
          description:
            ' The Epoch Unix Timestamp in seconds for the close of the Investment Window for the loan cycle ',
          stringType: StringTypeEnum.ETH_TIME,
        })
      ),
    redemptionRequestClosingTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('redemptionRequestClosingTimestamp')
      .meta(
        toFieldMetaData({
          propName: 'redemptionRequestClosingTimestamp',
          description:
            'The Epoch Unix Timestamp in seconds for the close of the Redemption Request Window for the loan cycle',
          stringType: StringTypeEnum.ETH_TIME,
        })
      ),
    maturingTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('maturingTimestamp')
      .meta(
        toFieldMetaData({
          propName: 'maturingTimestamp',
          description: ' The Epoch Unix Timestamp in seconds for the maturity date of the loan ',
          stringType: StringTypeEnum.ETH_TIME,
        })
      ),
    redemptionAvailableTimestamp: yup
      .number()
      .required()
      .default(0)
      .label('redemptionAvailableTimestamp')
      .meta(
        toFieldMetaData({
          propName: 'redemptionAvailableTimestamp',
          description: ' TThe Epoch Unix Timestamp in seconds for when vault tokens can be redeemed for USDC ',
          stringType: StringTypeEnum.ETH_TIME,
        })
      ),
    canRequestRedemption: yup
      .boolean()
      .required()
      .default(false)
      .label('canRequestRedemption')
      .meta(
        toFieldMetaData({
          description:
            ' If True, withdrawals can be requested; If False, withdrawals cannot be requested. Only False in the two days before maturity. ',
          propName: 'canRequestRedemption',
        })
      ),

    totalSupply: yupErc20WeiRequired
      .required()
      .default('0')
      .label('Total Supply')
      .meta(
        toFieldMetaData({
          propName: 'totalSupply',
          description:
            'The total number of vault tokens outstanding. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.000000 ',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),

    assetsFromPool: yupErc20WeiRequired
      .required()
      .default('0')
      .label('assetsFromPool')
      .meta(
        toFieldMetaData({
          propName: 'assetsFromPool',
          description: 'The amount of USDC-1e6 that is being transferred from the pool to the loan',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    assetToReturnToPool: yupErc20WeiRequired
      .required()
      .default('0')
      .label('assetToReturnToPool')
      .meta(
        toFieldMetaData({
          propName: 'assetToReturnToPool',
          description:
            'The amount of USDC-1e6 that is being transferred from the current loan to the pool. This is the amount that needs to be repaid to meet withdrawals.',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    assetsFromPriorLoan: yupErc20WeiRequired
      .required()
      .default('0')
      .label('assetsFromPriorLoan')
      .meta(
        toFieldMetaData({
          propName: 'assetsFromPriorLoan',
          description: 'The amount of USDC-1e6 that is being transferred from the prior loan to the current loan',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
  })

  .label('LoanCTO')
  .meta(toFieldMetaData({ propName: 'LoanCTO', description: 'LoanCTO', objectType: 'LoanCTO' }))

export interface LoanCTO {
  id: EthAddr
  symbol: string
  loanAddr: EthAddr
  borrowerAddr: EthAddr
  fundingVaultAddr: EthAddr
  poolAddr: EthAddr
  state: LoanLifeCycleState
  transitionState: LoanTransitionState
  durationDays: number
  principal: ERC20Wei
  startingPrincipal: ERC20Wei

  interest: ERC20Wei
  indicativeInterest: ERC20Wei
  originationFee: ERC20Wei

  indicativeApr: APR
  finalizedApr: APR
  exchangeRateAtDeposit: ERC20Wei
  exchangeRateAtMaturity: ERC20Wei
  fundingVaultBalance: ERC20Wei
  assetsRolloverToNextLoan: ERC20Wei
  assetsToReturnToPool: ERC20Wei
  accrualStartDayTimestamp: number
  accrualStartTimestamp: number
  transferInWindowDurationDays: number
  transferOutWindowDurationDays: number
  depositClosingTimestamp: number
  redemptionRequestClosingTimestamp: number
  maturingTimestamp: number
  redemptionAvailableTimestamp: number
  canRequestRedemption: boolean
  totalSupply: ERC20Wei
  assetsFromPool: ERC20Wei
  assetToReturnToPool: ERC20Wei
  assetsFromPriorLoan: ERC20Wei
}
export interface LoanCTOTypeCheck extends LoanCTO, Asserts<typeof LoanCTOSchema>, InferType<typeof LoanCTOSchema> {}
//
export const _testALoanCTO: LoanCTO = {} as unknown as InferType<typeof LoanCTOSchema>
export const _testBLoanCTO: InferType<typeof LoanCTOSchema> = {} as LoanCTO

export const TestLoanCTOSchema = yup.object({
  id: yup
    .string<EthAddr>()
    .label('LoanID loanAddr')
    .required()
    .meta(toFieldMetaData({ propName: 'id', description: 'ID of Loan ', stringType: StringTypeEnum.ID })),
})
export interface TestLoanCTO {
  id: string
}

type TestLoanCTOInfer = InferType<typeof TestLoanCTOSchema>
export const _testA: TestLoanCTO = {} as unknown as TestLoanCTOInfer
export const _testB: TestLoanCTOInfer = {} as unknown as TestLoanCTO
